<script setup lang="ts">
import type { Fund } from './fundConfig'

const props = defineProps<{
  fund: Fund
  signalTime: string | null
  execStatus: 'executed' | 'hold' | 'pending' | null
  execTime: string | null
  hasOfficialNav: boolean
  strikeLabel: string
}>()

// HL settlement is T+1 for EVERY fund (carved in stone 2026-10-01; CLAUDE.md "HL Settlement & Cutoff"): buy Monday →
// sell Tuesday via the HL app → cash later Tuesday → reinvest Wednesday. Fixed — never T+2/T+3/T+4, no per-fund override.
const settledLabel = 'T+1'

function fmtTime(iso: string | null): string {
  if (!iso) return '--'
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

const testId = `settlement-timeline-${props.fund.toLowerCase()}`
</script>

<template>
  <div class="timeline-container" :data-testid="testId">
    <div class="section-header">{{ props.fund }} SETTLEMENT TIMELINE</div>
    <div class="timeline">
      <div class="tl-step">
        <div class="tl-dot" :class="signalTime != null ? 'tl-done' : 'tl-pending'"></div>
        <div class="tl-label">Signal</div>
        <div class="tl-time">{{ fmtTime(signalTime) }}</div>
      </div>
      <div class="tl-line" :class="signalTime != null ? 'tl-line-done' : ''"></div>
      <div class="tl-step">
        <div class="tl-dot" :class="execStatus === 'executed' ? 'tl-done' : execStatus === 'hold' ? 'tl-hold' : 'tl-pending'"></div>
        <div class="tl-label">{{ execStatus === 'hold' ? 'Hold' : 'Dealt' }}</div>
        <div class="tl-time">{{ execTime ? fmtTime(execTime) : (execStatus === 'hold' ? 'No trade' : '--') }}</div>
      </div>
      <div class="tl-line" :class="hasOfficialNav ? 'tl-line-done' : ''"></div>
      <div class="tl-step">
        <div class="tl-dot" :class="hasOfficialNav ? 'tl-done' : 'tl-pending'"></div>
        <div class="tl-label">NAV Strike</div>
        <div class="tl-time">{{ hasOfficialNav ? strikeLabel : '--' }}</div>
      </div>
      <div class="tl-line"></div>
      <div class="tl-step">
        <div class="tl-dot tl-pending"></div>
        <div class="tl-label">Settled</div>
        <div class="tl-time">{{ settledLabel }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.section-header {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #666;
  margin-bottom: 0.5rem;
}
.timeline-container {
  border: 1px solid #222;
  border-radius: 4px;
  padding: 0.75rem;
  background: #111;
}
.timeline { display: flex; align-items: flex-start; }
.tl-step { flex: 1; text-align: center; }
.tl-dot { width: 10px; height: 10px; border-radius: 50%; margin: 0 auto 4px; }
.tl-done { background: #4caf50; box-shadow: 0 0 4px #4caf5066; }
.tl-hold { background: #ff9800; box-shadow: 0 0 4px #ff980066; }
.tl-pending { background: #0a0a0a; border: 1px solid #555; }
.tl-label { font-size: 8px; color: #888; }
.tl-time { font-size: 7px; color: #555; font-family: monospace; }
.tl-line { flex: 0.5; height: 1px; background: #333; margin-top: 5px; }
.tl-line-done { background: #4caf50; opacity: 0.4; }

@media (max-width: 768px) {
  .section-header { font-size: 0.7rem; }
  .tl-label { font-size: 10px; }
  .tl-time { font-size: 9px; }
}
</style>
